import { useCallback, useContext, useEffect, useState } from "react";
import {
  MantineTheme,
  useMantineTheme,
  Text,
  Title,
  Button,
} from "@mantine/core";
import { useApolloClient } from "@apollo/client";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { useModals } from "@mantine/modals";
import {
  GetMeetingSeriesPageQuery,
  GetMeetingSeriesPageQueryVariables,
} from "src/generated/graphql";
import { GetMeetingSeriesPage } from "src/api/graphql/queries/MeetingSeries";
import { AppContext } from "src/api/AppContext";
import {
  DEFAULT_ORDER,
  EMPLOYEE_ROLE_OBJ,
  ROUTE_NAMES,
} from "src/utils/constants";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import { ArrayElement } from "src/utils/helperTypes";
import MeetingSeriesModal from "./EventDetails";
import useWritePermission from "src/hooks/usePermission";
import { getTimeInAMPMFormat } from "src/utils/dateHelper";

export type MeetingSeries = ArrayElement<
  GetMeetingSeriesPageQuery["meetingSeriesPage"]["items"]
>;

const CalendarRecurringEvents = () => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.calendar);
  const modals = useModals();
  const { setCurrentPageTitle, currentLocation } = useContext(AppContext);
  const theme = useMantineTheme();
  const { query } = useApolloClient();
  const [refetchTableData, setrefetchTableData] = useState(false);

  const getRequests = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<MeetingSeries>> => {
    let { data } = await query<
      GetMeetingSeriesPageQuery,
      GetMeetingSeriesPageQueryVariables
    >({
      query: GetMeetingSeriesPage,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.meetingSeries_name,
          filters: fetchParams.filters,
        },
        locationId: currentLocation!.id,
      },
    });

    if (data.meetingSeriesPage) {
      return {
        rowCount: data.meetingSeriesPage.totalRows,
        rowData: data.meetingSeriesPage.items as MeetingSeries[],
      };
    }
    setrefetchTableData(false);
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.CALENDER_RECURRING_EVENTS);
    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleCreateModal = () => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Recurring Meetings",
      children: (
        // <Meeting
        //   refetch={() => setrefetchTableData(!refetchTableData)}
        //   meetings={[]}
        // />
        <MeetingSeriesModal
          isEdit={false}
          locationId={currentLocation?.id!}
          onRefetch={() => {
            setrefetchTableData(!refetchTableData);
          }}
          closeModal={() => modals.closeAll()}
          isAllowWrite={isAllowWrite}
        />
      ),
    });
  };
  const handleModal = (id: string) => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Recurring Event Details",
      children: (
        <MeetingSeriesModal
          seriesId={id}
          isEdit={true}
          locationId={currentLocation?.id!}
          onRefetch={() => {
            setrefetchTableData(!refetchTableData);
          }}
          closeModal={() => modals.closeAll()}
          isAllowWrite={isAllowWrite}
        />
      ),
    });
  };

  const ShowTable = useCallback(
    ({ theme }: { theme: MantineTheme }) => {
      return (
        <div>
          <ScrollableDataGrid<MeetingSeries>
            columnConfigs={[
              {
                field: "meetingSeries_name",
                name: "Name",
                filter: {
                  field: "name",
                  type: "text",
                },
                sortable: true,
                cellRenderer: (rowItem) => (
                  <Text
                    weight={500}
                    onClick={() => handleModal(rowItem.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {`${rowItem.name}`}
                  </Text>
                ),
              },
              {
                field: "scheduleFrom",
                name: "Schedule From",
                filter: {
                  field: "scheduleFrom",
                  type: "dateRange",
                },
                sortable: true,
                valueParser: (rowItem) => {
                  return rowItem.scheduleFrom
                    ? `${new Date(
                        rowItem.scheduleFrom
                      ).toDateString()}, ${new Date(
                        rowItem.scheduleFrom
                      ).toLocaleTimeString()}`
                    : "N/A";
                },
              },
              {
                field: "scheduleTo",
                name: "Schedule To",
                filter: {
                  field: "scheduleTo",
                  type: "dateRange",
                },
                sortable: true,
                valueParser: (rowItem) => {
                  return rowItem.scheduleTo
                    ? `${new Date(
                        rowItem.scheduleTo
                      ).toDateString()}, ${new Date(
                        rowItem.scheduleTo
                      ).toLocaleTimeString()}`
                    : "N/A";
                },
              },
            ]}
            dataFetch={getRequests}
          />
        </div>
      );
    },
    [currentLocation, refetchTableData]
  );

  if (!currentLocation?.id)
    return <Title order={3}>Please Select Location from Header</Title>;
  return (
    <>
      {isAllowWrite && (
        <Button style={{ marginBottom: 20 }} onClick={handleCreateModal}>
          Create +
        </Button>
      )}
      <ShowTable theme={theme} />
    </>
  );
};

export default CalendarRecurringEvents;
